/**
 * PromptCaptureLink — the room's "Context" affordance in an agent window header.
 *
 * Breaks the black-box effect (doc/arch/CONTEXT.md §13b): clicking it loads the
 * fully-assembled prompt the agent was actually given for this run — identity,
 * team composition, role contract, collaboration rules, tool API specs, and any
 * injected memory / room-context — and shows it in a modal. Read-only; the
 * agents persist these captures best-effort to S3 (RBAC-gated, 30-day TTL).
 *
 * Two sources, keyed differently (matches the S3 write keys):
 *   - worker  → (pool, agent, task)   [task = SFN execution name = turn.executionId]
 *   - builder → (run, agent)          [run  = turn.builderRunId — the runner's
 *                                       execution-name, captured from
 *                                       BUILDER_PHASE.payload.runId. NOT the
 *                                       turn's correlationId (a UUID staging id),
 *                                       which does NOT match the S3 capture key.]
 *
 * Renders nothing until we have the identifiers needed to address a capture —
 * an in-flight turn with no execution id yet simply has no link.
 */
import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X as XIcon, Loader2 } from 'lucide-react';
import { getPromptCapture } from '@stepflow/ui-shared/services/dataPlaneApi';

/** Render the assembled prompt body. Worker captures store ``prompt`` as either
 *  a two-block list (stable + variable) or a bare string; builder captures store
 *  ``stepflowContract`` (the StepFlow append on top of the Claude Code preset). */
function CaptureBody({ capture }) {
  if (!capture) return null;
  if (capture.source === 'builder') {
    return (
      <div className="space-y-3">
        <div className="rounded-md bg-slate-100 px-3 py-2 text-[11px] text-slate-500 ring-1 ring-slate-200">
          {capture.promptBase || 'Claude Code preset (base system prompt)'}
          <span className="mt-0.5 block text-slate-400">
            The base preset is managed by Claude Code; below is what StepFlow added for this agent.
          </span>
        </div>
        <Block label="StepFlow additions (role · team · tooling · skills)" text={capture.stepflowContract} />
      </div>
    );
  }
  // worker
  const p = capture.prompt;
  if (Array.isArray(p)) {
    return (
      <div className="space-y-3">
        {p.map((blk, i) => (
          <Block
            key={i}
            label={blk && blk._stepflow_variable ? 'Per-turn context (memory · attachments · room context)' : 'Agent identity · team · role · tools (cache-stable)'}
            text={typeof blk === 'string' ? blk : (blk && blk.text) || ''}
          />
        ))}
      </div>
    );
  }
  return <Block label="System prompt" text={typeof p === 'string' ? p : JSON.stringify(p, null, 2)} />;
}

function Block({ label, text }) {
  if (!text) return null;
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <pre className="whitespace-pre-wrap break-words rounded-md bg-slate-50 px-3 py-2 text-[12px] leading-relaxed text-slate-800 ring-1 ring-slate-200">
        {text}
      </pre>
    </div>
  );
}

export default function PromptCaptureLink({ networkId, source = 'worker', agent, pool, task, run }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [capture, setCapture] = useState(null);

  // Without the addressing key for this source there is nothing to fetch.
  const addressable = Boolean(
    networkId && agent && (source === 'builder' ? run : task),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setCapture(null);
    try {
      const res = await getPromptCapture(networkId, { source, agent, pool, task, run });
      if (res && res.capture) setCapture({ source: res.source, ...res.capture });
      else setError('No prompt was captured for this run yet.');
    } catch (e) {
      setError(e?.message || 'Failed to load the prompt.');
    } finally {
      setLoading(false);
    }
  }, [networkId, source, agent, pool, task, run]);

  const onOpen = useCallback(() => {
    setOpen(true);
    void load();
  }, [load]);

  if (!addressable) return null;

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
        title="Show the exact prompt this agent was given"
        data-testid="prompt-capture-link"
      >
        <FileText className="h-3 w-3 shrink-0" aria-hidden />
        Context
      </button>
      {open
        ? createPortal(
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4"
              onClick={() => setOpen(false)}
            >
              <div
                className="flex max-h-[82vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl"
                data-testid="prompt-capture-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="flex items-start justify-between gap-2 border-b border-slate-200 px-4 py-3">
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-slate-900">
                      Context — {agent}
                    </h2>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      The exact prompt this agent was given for this run.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"
                    aria-label="Close"
                  >
                    <XIcon className="h-5 w-5" aria-hidden />
                  </button>
                </header>
                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                  {loading ? (
                    <div className="flex items-center gap-2 py-6 text-[12px] text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Loading prompt…
                    </div>
                  ) : error ? (
                    <p className="py-4 text-[12px] text-slate-500">{error}</p>
                  ) : (
                    <CaptureBody capture={capture} />
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
